import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/useAuthStore';

const WaiterLogin: React.FC = () => {
  const [pin, setPin] = useState<string[]>(['', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const navigate = useNavigate();
  const { login } = useAuthStore();

  useEffect(() => {
    // Focus on first input
    inputRefs.current[0]?.focus();
  }, []);

  const handlePinChange = (index: number, value: string) => {
    // Only allow digits
    if (!/^\d*$/.test(value)) return;

    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);
    setError('');

    // Auto-advance to next input
    if (value && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 4 digits are entered
    if (index === 3 && value && newPin.every(digit => digit !== '')) {
      handleSubmit(newPin.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace') {
      if (!pin[index] && index > 0) {
        // Move to previous input if current is empty
        inputRefs.current[index - 1]?.focus();
      } else {
        // Clear current input
        const newPin = [...pin];
        newPin[index] = '';
        setPin(newPin);
      }
    }
  };

  const handleSubmit = async (submitPin?: string) => {
    const pinToSubmit = submitPin || pin.join('');
    if (pinToSubmit.length !== 4) {
      setError('אנא הזן 4 ספרות');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const success = await login('waiter', pinToSubmit);
      if (success) {
        navigate('/waiter/interface');
      } else {
        setError('קוד PIN שגוי');
        setPin(['', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch (error) {
      setError('שגיאה בהתחברות (ERR-102)');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4 watchlg:p-3 watch:p-2">
      {/* Optimized for small screens - smartwatch/phone */}
      <div className="w-full max-w-sm space-y-6 watchlg:space-y-4 watch:space-y-3">
        {/* Header - Minimal for small screens */}
        <div className="text-center">
          <div className="w-16 h-16 watchlg:w-12 watchlg:h-12 watch:w-10 watch:h-10 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4 watchlg:mb-3 watch:mb-2">
            <svg className="w-8 h-8 watchlg:w-6 watchlg:h-6 watch:w-5 watch:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <h1 className="text-xl watchlg:text-lg watch:text-base font-bold text-gray-900 dark:text-white">
            מלצר
          </h1>
          <p className="text-sm watchlg:text-sm watch:text-xs text-gray-600 dark:text-gray-300 mt-1">
            הזן קוד PIN
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 watchlg:p-4 watch:p-3">
          {/* PIN Input - LTR for correct digit order */}
          <div className="flex justify-center gap-2 mb-6 watchlg:mb-4 watch:mb-3" dir="ltr" style={{ direction: 'ltr' }}>
            {pin.map((digit, index) => (
              <input
                key={`pin-digit-${index}`}
                ref={el => inputRefs.current[index] = el}
                type="tel"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handlePinChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                style={{ direction: 'ltr' }}
                className="w-10 h-10 text-xl text-center border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 dark:focus:ring-purple-800 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white transition-all font-bold placeholder-gray-400"
                disabled={isLoading}
              />
            ))}
          </div>

          {/* Error Message */}
          {error && (
            <div className="text-center mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
              <p className="text-red-600 dark:text-red-400 text-sm font-medium">{error}</p>
            </div>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="text-center mb-4">
              <div className="inline-flex items-center">
                <svg className="animate-spin h-5 w-5 text-purple-600 ml-3" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span className="mr-3 text-gray-600 dark:text-gray-300 text-sm">מתחבר...</span>
              </div>
            </div>
          )}

          {/* Large touch-friendly button */}
          {pin.every(digit => digit !== '') && !isLoading && (
            <button
              onClick={() => handleSubmit()}
              className="w-full bg-purple-600 hover:bg-purple-700 active:bg-purple-800 text-white font-bold py-4 px-6 watchlg:py-3 watchlg:px-5 watchlg:text-base watch:py-3 watch:px-4 rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 text-lg watch:text-base"
            >
              כניסה
            </button>
          )}
        </div>

        {/* Back Button - Large for touch */}
        <button
          onClick={handleBackToHome}
          className="w-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium py-3 px-4 watchlg:py-2.5 watch:py-2 rounded-xl transition-colors text-sm watch:text-xs"
        >
          ← חזור לדף הבית
        </button>

        {/* Device info for smartwatch optimization - hidden on watch */}
        <div className="text-center watch:hidden">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            מותאם לשעון חכם ולטלפון
          </p>
        </div>
      </div>
    </div>
  );
};

export default WaiterLogin;